/**
 * Subdomain detection utility for routing between marketing site and application
 * 
 * Domain structure:
 * - talenture.ai (or www.talenture.ai) → Marketing site
 * - app.talenture.ai → Application dashboards
 */

export type SubdomainType = 'www' | 'app' | 'localhost';

/**
 * Get the current subdomain from window.location.hostname
 * Returns 'www' for marketing site, 'app' for application
 */
export function getSubdomain(): SubdomainType {
  if (typeof window === 'undefined') {
    return 'www';
  }
  
  const hostname = window.location.hostname;
  
  // Handle localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check for subdomain in localhost (e.g., app.localhost)
    const parts = hostname.split('.');
    if (parts[0] === 'app') {
      return 'app';
    }
    return 'localhost';
  }
  
  // Handle Replit development domains (*.replit.dev)
  if (hostname.includes('.replit.dev') || hostname.includes('.repl.co')) {
    // In development, use query param or default to localhost behavior
    const urlParams = new URLSearchParams(window.location.search);
    const subdomainParam = urlParams.get('subdomain');
    if (subdomainParam === 'app') {
      return 'app';
    }
    return 'localhost'; // Default to show everything in dev
  }
  
  // Handle production domains
  const parts = hostname.split('.');
  
  // Check for app.talenture.ai
  if (parts[0] === 'app') {
    return 'app';
  }
  
  // Check for www.talenture.ai or naked talenture.ai
  if (parts[0] === 'www' || parts.length === 2) {
    return 'www';
  }
  
  // Default to www for any other subdomain
  return 'www';
}

/**
 * Check if current subdomain is the marketing site (www or naked domain)
 */
export function isMarketingSite(): boolean {
  const subdomain = getSubdomain();
  return subdomain === 'www' || subdomain === 'localhost';
}

/**
 * Check if current subdomain is the application (app.talenture.ai)
 */
export function isAppSite(): boolean {
  return getSubdomain() === 'app';
}

/**
 * Get the full URL for the app subdomain
 * Used for cross-subdomain navigation after login
 */
export function getAppUrl(path: string = '/'): string {
  if (typeof window === 'undefined') {
    return path;
  }
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // In development, just use relative paths
  if (hostname === 'localhost' || hostname.includes('.replit.dev') || hostname.includes('.repl.co')) {
    return path;
  }
  
  // In production, construct app subdomain URL
  const parts = hostname.split('.');
  
  // If already on app subdomain, use relative path
  if (parts[0] === 'app') {
    return path;
  }
  
  // Construct app.talenture.ai URL
  const baseDomain = parts[0] === 'www' ? parts.slice(1).join('.') : hostname;
  return `${protocol}//app.${baseDomain}${path}`;
}

/**
 * Get the full URL for the marketing site (www)
 * Used for cross-subdomain navigation
 */
export function getMarketingUrl(path: string = '/'): string {
  if (typeof window === 'undefined') {
    return path;
  }
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // In development, just use relative paths
  if (hostname === 'localhost' || hostname.includes('.replit.dev') || hostname.includes('.repl.co')) {
    return path;
  }
  
  // In production, construct www subdomain URL
  const parts = hostname.split('.');
  
  // If already on www or naked domain, use relative path
  if (parts[0] === 'www' || parts.length === 2) {
    return path;
  }
  
  // Construct www.talenture.ai URL from app.talenture.ai
  if (parts[0] === 'app') {
    const baseDomain = parts.slice(1).join('.');
    return `${protocol}//www.${baseDomain}${path}`;
  }
  
  return path;
}
